import { find } from 'lodash';
import React, { Component } from 'react';
import styled from 'styled-components';

// Components:
import { CashBack } from '@lt/components';
import {
  PaymentSkeletonMobile,
  PaymentSkeletonDesktop,
} from '@lt/components/icons-react/skeletons';
import { i18n } from '@lt/localization';
import { PAYMENT_METHOD_MIR_CARD } from 'src/store/payment/constants';
import type {
  CashbackBonuses,
  DiscountMethod,
  PaymentMethod as PaymentMethodType,
} from 'checkout/types/payment';
import { TourGuide } from 'src/components/Banners/TourGuide/TourGuide';
import Discount from './Discount';
import PriceChangePopup from './PriceChangePopup';
import PaymentMethod from './PaymentMethod/PaymentMethod';

// Constants:
import { DISCOUNT_BONUS } from '../../store/constants/discount';

const baseCls = 'checkout-payment';

interface PaymentProps {
  discountMethods?: DiscountMethod[];
  paymentsMethods: PaymentMethodType[];
  orderPayment: string | null;
  changeOrderPayment: (method: string) => void;
  bonuses: CashbackBonuses;
  isCreated?: boolean;
  isMobile?: boolean;
  priceWithDiscount: number;
  submit: () => void;
  customCashbackIconURL?: string;
  customCashbackText?: string;
  customCashbackTooltipText?: string;
  activateCashbackForPartner?: boolean;
  isHotelOnly: boolean;
}

class Payment extends Component<PaymentProps> {
  componentDidMount() {
    const { paymentsMethods, orderPayment, changeOrderPayment } = this.props;

    /**
     * Если способ оплаты не выбран, установить первый из доступных
     */
    if (this.isAnyPaymentMethodsEnabled() && !orderPayment) {
      const availablePaymentMethods = paymentsMethods.filter(
        (method) => method.enable,
      );
      const [defaultMethod] = availablePaymentMethods;

      changeOrderPayment(defaultMethod.type);
    }
  }

  onSetOrder = () => {
    const { submit } = this.props;

    submit();
  };

  getDiscountLabel = (hasLoyalty) =>
    i18n.t('checkout.payment.discount', {
      context: hasLoyalty ? 'loyalty' : '',
    });

  isAnyPaymentMethodsEnabled = () => {
    const { paymentsMethods } = this.props;
    return paymentsMethods.some((item) => item.enable);
  };

  isDiscountAvailable = () => {
    const { discountMethods = [], isCreated } = this.props;
    return !isCreated && discountMethods.some((item) => item.enable);
  };

  render() {
    const {
      paymentsMethods,
      orderPayment,
      changeOrderPayment,
      bonuses,
      isMobile,
      priceWithDiscount,
      discountMethods,
      customCashbackIconURL,
      customCashbackText,
      customCashbackTooltipText,
      activateCashbackForPartner,
      isHotelOnly,
    } = this.props;

    const isAnyPaymentMethodsEnabled = this.isAnyPaymentMethodsEnabled();

    const hasLoyalty = Boolean(
      find(discountMethods, (discount) => discount.type === DISCOUNT_BONUS),
    );

    const hasMirPayment = Boolean(
      paymentsMethods.find((method) =>
        method.type.includes(PAYMENT_METHOD_MIR_CARD),
      ),
    );

    const isSuperCashback =
      bonuses &&
      bonuses.cashback_identifier &&
      ![null, 'standard1', 'standard2'].includes(bonuses.cashback_identifier);

    const allowedMethods = discountMethods?.filter(
      (discountMethod) => discountMethod.enable,
    );
    const anyMethodsAllowed = Number(allowedMethods?.length) > 0;

    return (
      <div>
        {anyMethodsAllowed && <Discount />}

        {priceWithDiscount !== 0 && (
          <PaymentHeader>
            <PaymentTitle hasLoyalty={hasLoyalty} id={PAYMENT_METHOD_MIR_CARD}>
              {i18n.t('checkout.payment.title')}
            </PaymentTitle>
            {hasLoyalty && activateCashbackForPartner ? (
              <InfoCashback>
                <span>
                  {i18n.t('checkout.payment.cashback', {
                    context: isSuperCashback ? 'super' : '',
                  })}
                </span>
                <CashBack
                  customCashbackIconURL={customCashbackIconURL}
                  customCashbackText={customCashbackText}
                  customCashbackTooltipText={customCashbackTooltipText}
                  count={`+${bonuses.bonus_total}`}
                  isMobile={isMobile}
                  size="m"
                  type={bonuses.cashback_identifier || undefined}
                />
              </InfoCashback>
            ) : null}
          </PaymentHeader>
        )}
        <StyledTourGuide
          mode="payment"
          isMobile={isMobile}
          isHotelOnly={isHotelOnly}
        />

        {!isAnyPaymentMethodsEnabled && isMobile && <PaymentSkeletonMobile />}
        {!isAnyPaymentMethodsEnabled && !isMobile && <PaymentSkeletonDesktop />}

        <PriceChangePopup />

        {priceWithDiscount > 0 && (
          <StyledPaymentMethod
            $isMobile={!!isMobile}
            $isHotelOnly={isHotelOnly}
            className={baseCls}
          >
            {paymentsMethods.map((item) => (
              <PaymentMethod
                key={item.type}
                item={item}
                isActive={orderPayment === item.type}
                isDisabled={!item.enable_for_toggle}
                onChange={changeOrderPayment}
                hasMirPayment={hasMirPayment}
              />
            ))}
          </StyledPaymentMethod>
        )}
      </div>
    );
  }
}

const StyledTourGuide = styled(TourGuide)`
  margin: 1px 0 8px 0;
`;

const PaymentTitle = styled.div<{ hasLoyalty?: boolean }>`
  margin-bottom: 8px;
  display: inline-block;
  width: ${({ hasLoyalty }) => (hasLoyalty ? '100%' : 'inherit')};
  font-size: 22px;
  line-height: 26px;
  font-weight: bold;
`;

const InfoCashback = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 16px;
  gap: 7px;
`;

const PaymentHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-left: 10px;
  margin-bottom: 7px;
`;

const StyledPaymentMethod = styled.div<{
  $isMobile: boolean;
  $isHotelOnly: boolean;
}>`
  ${({ $isMobile, $isHotelOnly }) =>
    !$isMobile &&
    $isHotelOnly &&
    'box-shadow: 0px 1px 2px rgba(210, 208, 197, 0.9), 0px 10px 15px rgba(216, 214, 204, 0.9);'}
`;

export default Payment;
